import { createCloudinaryProductImageUrl } from './Pdf'
import {
  type InvoicePDFInfoItemRow,
  type InvoicePDFColConfig,
  createDocumentDefinition,
  type InvoicePDFInfo,
} from './PdfInvoice'
import { type IProjectPriceCalculation } from '@/pages/projects/components/ProjectProductFooter.vue'
import { type IProjectProductItem } from '@/pages/projects/types'
import { translate as $t } from '@/plugins/translation'
import type { Content } from 'pdfmake/interfaces'
import { omit } from 'radash'
import wrap from 'word-wrap'

export interface ICreateProjectPdfDefinitionConfig {
  hideFields: string[]
  selectedTag?: string
}

export const createTableRow = (item: IProjectProductItem, style: string = 'tableRow', index?: number) =>
  ({
    style,
    border: index !== undefined,
    cols: {
      index: { content: index === undefined ? '' : `${index + 1}` },
      image: {
        content: {
          margin: [0, 1, 0, 1],
          image: createCloudinaryProductImageUrl(item.image),
          width: 30,
        },
      },
      product: {
        content: {
          margin: [0, 6, 0, 0],
          stack: [
            { text: item.brand },
            {
              text: item.title,
              bold: true,
              margin: [0, 1, 0, item.description && (item.isExternal || item.pConInformation) ? 0 : 2],
            },
            ...((item.isExternal || item.pConInformation) && item.description
              ? [
                  {
                    text: item.description,
                    italics: true,
                    fontSize: 7,
                    margin: [0, 2, 0, 2],
                  } as Content,
                ]
              : []),
          ],
        },
      },
      comment: {
        content: !item.comment ? '' : wrap(item.comment, { width: 15, newline: '\n', cut: true }),
        style: { italics: true },
      },
      tags: { content: !item.tags ? '' : `${item.tags.map((t) => t.name).join(', ')}` },
      quantity: { content: item.quantity },
      retailPriceNetString: { content: item.retailPriceNetString },
      retailPriceTotalNetString: { content: item.retailPriceTotalNetString },
    },
  }) as InvoicePDFInfoItemRow

export const createTableRows = (items: IProjectProductItem[], showAlternatives: boolean) =>
  items.flatMap<InvoicePDFInfoItemRow>((item, index) =>
    item.alternatives && showAlternatives
      ? [
          createTableRow(item, '', index),
          ...item.alternatives.map((alternative) => createTableRow(alternative, 'tableCellShallow')),
        ]
      : [createTableRow(item, '', index)],
  )

export const tableConfig: InvoicePDFColConfig[] = [
  {
    id: 'index',
    label: '#',
    width: 'auto',
    style: 'tableCellIndex',
  },
  {
    id: 'image',
    label: '',
    width: 'auto',
  },
  {
    id: 'product',
    label: $t('Product'),
    width: '*',
    style: 'noMargin',
  },
  {
    id: 'tags',
    label: $t('Tags'),
    width: 'auto',
  },
  {
    id: 'comment',
    label: $t('Comment'),
    width: 'auto',
  },
  {
    id: 'quantity',
    label: $t('Quantity'),
    width: 'auto',
  },
  {
    id: 'retailPriceNetString',
    label: $t('retailPriceNetString'),
    width: 'auto',
    alignment: 'right',
  },
  {
    id: 'retailPriceTotalNetString',
    label: $t('retailPriceTotalNetString'),
    width: 'auto',
    style: 'tableCellBold',
    alignment: 'right',
  },
]

export const createProjectPdfDefinition = async (
  title: string,
  titleSuffix: string = '',
  items: IProjectProductItem[],
  pricing: IProjectPriceCalculation,
  hideFields: string[],
) => {
  const pdfInfoProject: InvoicePDFInfo = {
    info: {
      title: title,
    },
    logo: {
      svg: `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="30" fill="none" viewBox="0 0 160 30"><path fill="#000" d="M10.247 2.487c2.848 0 5.242.918 7.185 2.753 2.04 1.949 3.062 4.28 3.062 6.991v14.576L15.803 29h-.247V12.232c0-1.868-.515-3.236-1.543-4.105-1.03-.869-2.284-1.304-3.766-1.304-1.482 0-2.737.435-3.765 1.304-1.03.869-1.544 2.237-1.544 4.105v14.576L.247 29H0V12.232c0-2.68 1.02-5.01 3.062-6.992 1.91-1.835 4.304-2.753 7.185-2.753Zm139.506 0c2.848 0 5.243.918 7.185 2.753C158.979 7.19 160 9.52 160 12.232v14.576L155.309 29h-.247V12.232c0-1.868-.515-3.236-1.543-4.105-1.029-.869-2.284-1.304-3.766-1.304-1.481 0-2.737.435-3.765 1.304-1.029.869-1.543 2.237-1.543 4.105v14.576L139.753 29h-.247V12.232c0-2.68 1.021-5.01 3.062-6.992 1.909-1.835 4.304-2.753 7.185-2.753ZM32.839 2v16.768c0 1.868.515 3.236 1.544 4.105 1.028.87 2.284 1.303 3.765 1.303 1.481 0 2.737-.434 3.765-1.303 1.029-.869 1.544-2.237 1.544-4.105V4.192L48.148 2h.247v16.768c0 2.68-1.02 5.01-3.062 6.992-1.91 1.835-4.305 2.753-7.185 2.753-2.848 0-5.243-.918-7.185-2.753-2.041-1.949-3.062-4.28-3.062-6.992V4.192L32.593 2h.246Zm27.902 0v16.768c0 1.868.514 3.236 1.543 4.105 1.028.87 2.284 1.303 3.765 1.303 1.482 0 2.737-.434 3.766-1.303 1.028-.869 1.543-2.237 1.543-4.105V4.192L76.049 2h.247v16.768c0 2.68-1.02 5.01-3.061 6.992-1.91 1.835-4.305 2.753-7.186 2.753-2.848 0-5.243-.918-7.185-2.753-2.041-1.949-3.061-4.28-3.061-6.992V4.192L60.493 2h.248Zm33.21.487c2.814 0 5.21.918 7.185 2.753 2.041 1.9 3.062 3.995 3.062 6.285v.049l-4.692 2.192h-.247v-1.534c0-1.868-.514-3.236-1.543-4.105-1.029-.869-2.284-1.304-3.765-1.304-1.482 0-2.737.435-3.765 1.304-1.03.869-1.544 2.237-1.544 4.105v6.536c0 1.868.514 3.236 1.544 4.105 1.028.87 2.283 1.303 3.765 1.303 1.481 0 2.736-.438 3.765-1.315s1.543-2.022 1.543-3.435l4.692-2.192h.247v1.534c0 2.68-1.021 5.01-3.062 6.992-1.91 1.836-4.305 2.753-7.185 2.753-2.848 0-5.243-.917-7.186-2.753-2.04-1.949-3.061-4.28-3.061-6.992v-6.536c0-2.696 1.02-5.027 3.061-6.992 1.91-1.835 4.305-2.753 7.186-2.753Zm38.148 10.46v5.821c0 2.68-1.021 5.01-3.062 6.992-1.909 1.835-4.305 2.753-7.185 2.753-2.848 0-5.243-.918-7.185-2.753-1.05-1.003-1.82-2.11-2.329-3.316l-.132-.331 4.194-1.957h.258c.201 1.173.675 2.08 1.429 2.717 1.028.869 2.283 1.303 3.765 1.303 1.481 0 2.736-.434 3.765-1.303.972-.82 1.485-2.086 1.539-3.798l.005-.307v-3.632l4.691-2.188h.247Zm-10.247-10.46c2.847 0 5.242.918 7.185 2.753 1.059 1.012 1.838 2.129 2.347 3.346l-4.168 1.948h-.247c-.231-1.006-.675-1.835-1.352-2.407-1.029-.869-2.284-1.303-3.765-1.303-1.482 0-2.737.434-3.766 1.303-1.028.869-1.543 2.237-1.543 4.105v3.278l-4.701 2.193h-.237v-5.471c0-2.68 1.02-5.01 3.061-6.992 1.91-1.835 4.305-2.752 7.186-2.752Z"/></svg>`,
      width: 80,
      alignment: 'right',
      margin: [0, 0, 0, 30],
    },
    titleContent: !hideFields.includes('projectTitle')
      ? [{ text: $t('Project') + ': ', bold: true }, { text: title }, { text: titleSuffix ? ` ${titleSuffix}` : '' }]
      : '',
    addressContent: [],
    headInfoContent: [],
    belowTableContent: [],
    itemTableConfig: {
      colConfigs: tableConfig.filter((configItem) => !configItem.id || !hideFields!.includes(configItem.id)),
      rowsOrGroups: createTableRows(items, !hideFields.includes('alternatives')),
    },
    tableFooter: hideFields.includes('totalRrpNet')
      ? undefined
      : [
          [
            '',
            { text: `${$t('RRP sum net')}:`, bold: true },
            { text: pricing.totalRrpNet, bold: true, alignment: 'right', fontSize: 9 },
          ],
        ],

    pageFooter: {
      columns: [
        {
          text: 'nuucon GmbH \n Jarrestraße 42, 22303 Hamburg \n www.nuucon.com',
        },
        {
          text: 'Managing Directors \n Pierre Haarfeld and Philipp Renerig \n T+49 40 228 582 10-0 \n business@nuucon.com',
        },
        { text: 'Ust-ID: DE 313871675 \n HRB-Nr: 147877 \n Amtsgericht Hamburg' },
        { text: '' },
      ],
    },
  }
  return createDocumentDefinition(omit<any, any>(pdfInfoProject, hideFields) as any)
}
